
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { updateUserStreak } from '@/utils/streak-utils';

export interface DashboardData {
  streak: {
    current: number;
    max: number;
  };
  weeklyProgress: number;
  moduleProgress: {
    completed: number;
    total: number;
  };
  upcomingWorkouts: {
    id: string;
    title: string;
    date: string;
    time: string;
    duration?: string;
    day?: string;
  }[];
  recentActivities: {
    id: string;
    type: 'workout' | 'lesson' | 'practice';
    title: string;
    date: string;
    completed: boolean;
    time?: string;
    points?: number;
  }[];
}

export const useDashboardData = () => {
  const { user } = useAuth();
  const [isUpdatingStreak, setIsUpdatingStreak] = useState(false);

  // Update the user's streak when they view the dashboard
  useEffect(() => {
    const updateStreak = async () => {
      if (user?.id && !isUpdatingStreak) {
        setIsUpdatingStreak(true);
        try {
          await updateUserStreak(user.id);
        } catch (error) {
          console.error('Error updating streak:', error);
        } finally {
          setIsUpdatingStreak(false);
        }
      }
    };

    updateStreak();
  }, [user?.id, isUpdatingStreak]);

  return useQuery({
    queryKey: ['dashboardData', user?.id],
    queryFn: async (): Promise<DashboardData> => {
      // If no user is found, return default empty data instead of throwing
      if (!user) {
        console.log('No user found, returning default data');
        return {
          streak: { current: 0, max: 0 },
          weeklyProgress: 0,
          moduleProgress: { completed: 0, total: 0 },
          upcomingWorkouts: [],
          recentActivities: []
        };
      }

      try {
        // Fetch user streak data
        const { data: streakData, error: streakError } = await supabase
          .from('user_streaks')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (streakError && streakError.code !== 'PGRST116') {
          console.error('Error fetching streak data:', streakError);
        }

        // Calculate weekly progress using the database function
        const { data: weeklyProgressData, error: weeklyProgressError } = await supabase
          .rpc('calculate_weekly_progress', { user_uuid: user.id });

        if (weeklyProgressError) {
          console.error('Error calculating weekly progress:', weeklyProgressError);
        }

        // Count completed modules using the database function
        const { data: moduleProgressData, error: moduleProgressError } = await supabase
          .rpc('count_completed_modules', { user_uuid: user.id });

        if (moduleProgressError) {
          console.error('Error counting completed modules:', moduleProgressError);
        }

        // Fix the type issue - moduleProgressData is now properly accessed
        let completedModules = 0;
        let totalModules = 0;
        
        if (moduleProgressData && moduleProgressData.length > 0) {
          completedModules = moduleProgressData[0].completed_count || 0;
          totalModules = moduleProgressData[0].total_count || 0;
        }

        // Fetch upcoming workouts
        const { data: upcomingWorkoutsData, error: upcomingWorkoutsError } = await supabase
          .from('workouts')
          .select('id, title, date_assigned, time_assigned')
          .eq('user_id', user.id)
          .eq('completed', false)
          .order('date_assigned', { ascending: true })
          .limit(3);

        if (upcomingWorkoutsError) {
          console.error('Error fetching upcoming workouts:', upcomingWorkoutsError);
        }

        // Fetch recent activities
        const { data: recentActivitiesData, error: recentActivitiesError } = await supabase
          .from('user_activities')
          .select('id, activity_type, title, date, completed, points')
          .eq('user_id', user.id)
          .order('date', { ascending: false })
          .limit(5);

        if (recentActivitiesError) {
          console.error('Error fetching recent activities:', recentActivitiesError);
        }

        return {
          streak: {
            current: streakData?.current_streak || 0,
            max: streakData?.max_streak || 0,
          },
          weeklyProgress: weeklyProgressData || 0,
          moduleProgress: {
            completed: completedModules,
            total: totalModules,
          },
          upcomingWorkouts: (upcomingWorkoutsData || []).map((workout) => ({
            id: workout.id,
            title: workout.title,
            date: new Date(workout.date_assigned).toLocaleDateString(),
            time: workout.time_assigned || '',
            // Add missing properties used in Dashboard.tsx
            duration: '30 min', // Default value
            day: new Date(workout.date_assigned).toLocaleDateString('en-US', { weekday: 'short' }),
          })),
          recentActivities: (recentActivitiesData || []).map((activity) => ({
            id: activity.id,
            // Cast the activity_type to ensure it's one of the allowed values
            type: (activity.activity_type || 'lesson') as 'workout' | 'lesson' | 'practice',
            title: activity.title || 'Untitled Activity',
            date: activity.date ? new Date(activity.date).toLocaleDateString() : new Date().toLocaleDateString(),
            completed: !!activity.completed,
            // Add missing properties used in Dashboard.tsx
            time: activity.date ? new Date(activity.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
            points: activity.points || 5, // Default value
          })),
        };
      } catch (error) {
        console.error('Error in dashboard data fetch:', error);
        // Return default data instead of throwing to prevent infinite loading
        return {
          streak: { current: 0, max: 0 },
          weeklyProgress: 0,
          moduleProgress: { completed: 0, total: 0 },
          upcomingWorkouts: [],
          recentActivities: []
        };
      }
    },
    enabled: true, // Always enable the query but handle no-user case inside
    retry: 1, // Limit retries to prevent excessive requests
    staleTime: 60000, // Cache for 1 minute
  });
};
